import { parseCookie, stringifySetCookie } from "cookie";
import { all, call, delay, put, takeLatest } from "redux-saga/effects";

import {
    STROLLER_PRODUCT_PHOTO_CHECK_HIDE_THUMBS,
    STROLLER_PRODUCT_PHOTO_FETCH_TRIGGER,
    STROLLER_PRODUCT_PHOTO_TOGGLE_THUMBS_TRIGGER,
    StrollerProductPhotoCheckHideThumbsAction,
    StrollerProductPhotoFetchNormalizedResponse,
    StrollerProductPhotoFetchTriggerAction,
    StrollerProductPhotoToggleThumbsTriggerAction,
    strollerProductPhotoFetchApi,
    strollerProductPhotoUpdateThumbs,
} from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.actions";
import { STROLLER_PRODUCT_PHOTO_HIDE_THUMBNAILS_COOKIE } from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhotoPage";
import {
    XHRAction,
    XHRError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import { PhotoDetailSchema, StrollerPhoto } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { strollers_api_v2_get_stroller_product_photos_url } from "@/mk/urls/functions";

const logger = getLogger("strollers/StrollerProductPhoto.sagas");

const getStrollerPhotosHideThumbnailsCookie = (): boolean => {
    if (typeof window !== "undefined") {
        return (
            parseCookie(document.cookie)[STROLLER_PRODUCT_PHOTO_HIDE_THUMBNAILS_COOKIE] ===
                String(true) || false
        );
    }
    return false;
};

interface FetchApiResponse {
    body: StrollerProductPhotoFetchNormalizedResponse;
}

function* fetch({
    strollerSlug,
    strollerVariantId,
    photoId,
    xhr,
}: StrollerProductPhotoFetchTriggerAction & XHRAction) {
    yield put(strollerProductPhotoFetchApi.request(strollerSlug, strollerVariantId, photoId));

    try {
        const response: FetchApiResponse = yield call(() =>
            xhr.get(
                strollers_api_v2_get_stroller_product_photos_url({
                    strollerSlug,
                    strollerVariantId,
                }),
            ),
        );

        const strollerPhotos: StrollerPhoto[] = getDenormalizedEntities<StrollerPhoto>(
            response.body,
            PhotoDetailSchema,
            response.body.result.strollerPhotos,
        );
        const strollerPhoto = strollerPhotos.find((p) => p.id === photoId);
        if (!strollerPhoto) {
            const err = new Error(
                "Photo not found",
            ) as XHRError<StrollerProductPhotoFetchNormalizedResponse>;
            err.status = 404;
            err.response = response;
            throw err;
        }

        yield put(
            strollerProductPhotoFetchApi.success(
                strollerSlug,
                strollerVariantId,
                photoId,
                response.body,
            ),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(
            strollerProductPhotoFetchApi.failure(
                strollerSlug,
                strollerVariantId,
                photoId,
                normalizeError(error),
            ),
        );
    }
}

function* toggleThumbs({ xhr }: StrollerProductPhotoToggleThumbsTriggerAction & XHRAction) {
    const currentValue = getStrollerPhotosHideThumbnailsCookie();

    // when changing showThumbnails then the ImageGalleryLoadable need to be unmounted and re-rendered (to avoid buggy
    // behaviour of showThumbnail) */}
    yield put(strollerProductPhotoUpdateThumbs(null));
    yield delay(500);

    const newValue = !currentValue;
    document.cookie = stringifySetCookie({
        name: STROLLER_PRODUCT_PHOTO_HIDE_THUMBNAILS_COOKIE,
        value: String(newValue),
        path: "/",
    });
    yield put(strollerProductPhotoUpdateThumbs(newValue));
}

function* checkHideThumbs({ xhr }: StrollerProductPhotoCheckHideThumbsAction & XHRAction) {
    const currentValue = getStrollerPhotosHideThumbnailsCookie();
    yield put(strollerProductPhotoUpdateThumbs(currentValue));
}

export default function* root() {
    yield all([
        takeLatest(STROLLER_PRODUCT_PHOTO_FETCH_TRIGGER, fetch),
        takeLatest(STROLLER_PRODUCT_PHOTO_TOGGLE_THUMBS_TRIGGER, toggleThumbs),
        takeLatest(STROLLER_PRODUCT_PHOTO_CHECK_HIDE_THUMBS, checkHideThumbs),
    ]);
}
